import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import Joi from 'joi';

import { tunnelActions } from 'reducers';
import { validateSchema } from 'utils/helpers';
import { NAME_REGEX, stepIDs } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { TunnelBox } from 'containers/tunnel/common';
import {
    MemoizedFiscalAddressForm,
    MemoizedFiscalSituationFormFCPRPP,
    MemoizedGeneralInfoForm,
    MemoizedPoliticallyExposedForm,
    MemoizedSituationFormFPCIPP,
} from './memoized-components/MemoizedComponents';
import { ErrorMessage, FullAddressForm, SubmitButton, SuccessMessage } from 'components';

const nameSchema = {
    lastName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
        'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_NOM,
        'string.min': texts.LAST_NAME_LENGTH,
        'string.max': texts.LAST_NAME_LENGTH,
        'string.pattern.base': texts.LAST_NAME_PATTERN,
    }),
    firstName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
        'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_PRENOM,
        'string.min': texts.FIRST_NAME_LENGTH,
        'string.max': texts.FIRST_NAME_LENGTH,
        'string.pattern.base': texts.FIRST_NAME_PATTERN,
    }),
    birthName: Joi.string().trim().max(50).pattern(NAME_REGEX).allow('').messages({
        'string.max': texts.VEUILLEZ_SAISIR_VOTRE_NOM,
        'string.pattern.base': texts.BIRTHNAME_PATTERN,
    }),
};

const schema = {
    noOfPersons: Joi.number().min(0).max(19).required().messages({
        'string.empty': texts.VEUILLEZ_SAISIR_LE_NOMBRE_DE_PERSONNES,
    }),
    hasDifferentFiscalAddress: Joi.boolean().valid(true, false),
    fiscalAddress: Joi.when('hasDifferentFiscalAddress', {
        is: 'true',
        then: {
            address: Joi.string().required().messages({
                'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_ADDRESS,
            }),
            city: Joi.string()
                .required()
                .messages({ 'string.empty': texts.VEUILLEZ_SAISIR_LA_VILLE }),
            zipCode: Joi.string().required().messages({
                'string.empty': texts.VEUILLEZ_SAISIR_LE_CODE_POSTAL,
            }),
        },
        otherwise: Joi.optional(),
    }),
    wasUSFiscalResident: Joi.string().valid('true', 'false').required(),
    tin: Joi.when('wasUSFiscalResident', {
        is: 'true',
        then: Joi.string().trim().required().messages({
            'string.empty': texts.VEUILLEZ_SAISIR_TIN,
        }),
        otherwise: Joi.any(),
    }),
    nifReason: Joi.when('nif', {
        is: '',
        then: Joi.string().required().messages({
            'string.empty': texts.VEUILLEZ_SAISIR_NIF_OR_MOTIF_ABSENCE_NIF,
        }),
        otherwise: Joi.any(),
    }),
    fiscalObligationOtherCountry: Joi.string().valid('true', 'false').required(),
    fiscalObligations: Joi.when('fiscalObligationOtherCountry', {
        is: 'true',
        then: Joi.array().items({
            country: Joi.string().required(),
            nifReason: Joi.when('nif', {
                is: '',
                then: Joi.string().required(),
                otherwise: Joi.any(),
            }),
        }),
        otherwise: Joi.any(),
    }),
    politicallyExposedPerson: Joi.string().valid('true', 'false').required(),
};

const ContactInfoFormPP = ({
    isStepVerified,
    isPhoneVerified,
    errors,
    forms,
    callbacks,
    changesMade,
    handleNextStep,
    tunnelErrors,
    submitLoader,
    tunnelSubscription,
    showSuccessMessage,
    areDefaultFieldsCompleted,
    validateDefaultSchema,
    onAdditionalErrors,
    onSubmit,
    isOnClientSpace,
    isOnAdminSpace,
}) => {
    const dispatch = useDispatch();
    const { stepId } = useParams();

    const submitButtonText = useMemo(() => {
        if (isOnClientSpace || isOnAdminSpace) {
            return texts.SAVE;
        }
        return isStepVerified && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT;
    }, [isOnClientSpace, isOnAdminSpace, isStepVerified, changesMade]);

    const areSpecificFieldsCompleted = useMemo(
        () =>
            (forms.hasDifferentFiscalAddress === false ||
                (forms.fiscalAddress?.address !== '' &&
                    forms.fiscalAddress?.city !== '' &&
                    forms.fiscalAddress?.zipCode !== '' &&
                    forms.fiscalAddress?.country !== '')) &&
            forms.situation.situation &&
            forms.situation.noOfPersons !== '' &&
            forms.situation.matrimonialRegime !== '' &&
            forms.situation.activitySector !== '' &&
            forms.fiscalSituation.wasUSFiscalResident !== null &&
            (forms.fiscalSituation.wasUSFiscalResident === 'false' ||
                forms.fiscalSituation.tin.trim() !== '') &&
            (forms.fiscalSituation.nif !== '' || forms.fiscalSituation.nifReason !== '') &&
            forms.fiscalSituation.fiscalObligationOtherCountry !== null &&
            (forms.fiscalSituation.fiscalObligationOtherCountry === 'false' ||
                forms.fiscalObligations?.every(
                    fo => fo.country !== '' && (fo.nif !== '' || fo.nifReason !== '')
                )) &&
            forms.politicallyExposed.politicallyExposedPerson !== null &&
            (forms.politicallyExposed.politicallyExposedPerson === 'false' ||
                Object.values(forms.politicallyExposed.politicallyExposedReason).some(
                    per => per === true
                )),
        [forms]
    );

    const allowSubmit = useMemo(
        () => areDefaultFieldsCompleted && areSpecificFieldsCompleted,
        [areDefaultFieldsCompleted, areSpecificFieldsCompleted]
    );

    const formIsValid = useCallback(() => {
        const isDefaultSchemaValid = validateDefaultSchema();
        if (tunnelSubscription?.indirectUntransferred) return isDefaultSchemaValid;
        let validationSchema =
            +stepId === stepIDs.CO_SUBSCRIBER_CONTACT_INFO || !stepId
                ? { ...schema, ...nameSchema }
                : schema;
        let { valid, newErrors } = validateSchema(validationSchema, {
            hasDifferentFiscalAddress: forms.hasDifferentFiscalAddress,
            fiscalAddress: forms.fiscalAddress,
            ...forms.situation,
            ...forms.fiscalSituation,
            ...forms.politicallyExposed,
            ...forms.generalInfo,
        });
        if (forms.politicallyExposed.politicallyExposedPerson === 'true') {
            const containsAtLeastOneTrue = Object.keys(
                forms.politicallyExposed.politicallyExposedReason
            ).some(key => forms.politicallyExposed.politicallyExposedReason[key] === true);
            valid = valid && containsAtLeastOneTrue;
        }
        onAdditionalErrors(newErrors);

        return isDefaultSchemaValid && valid;
    }, [validateDefaultSchema, tunnelSubscription, stepId, forms, onAdditionalErrors]);

    const handleSubmit = useCallback(() => {
        onSubmit(formIsValid());
    }, [formIsValid, onSubmit]);

    useEffect(() => {
        dispatch(tunnelActions.setValidationHandler(formIsValid));
        return () => dispatch(tunnelActions.setValidationHandler());
    }, [dispatch, formIsValid]);

    return (
        <TunnelBox>
            {(!!Object.keys(tunnelErrors).length ||
                Object.keys(errors).find(key => errors[key] !== '')) && (
                <ErrorMessage gutterBottom message={texts.FORM_HAS_ERRORS} />
            )}
            <MemoizedGeneralInfoForm
                coSubscriber={+stepId === stepIDs.CO_SUBSCRIBER_CONTACT_INFO}
                generalInfo={forms.generalInfo}
                onChange={callbacks.handleGeneralInfoChange}
                errors={errors}
                tunnelErrors={tunnelErrors}
                isPhoneVerified={isPhoneVerified}
                displayAllFields={isOnClientSpace || isOnAdminSpace}
                isOnAdminSpace={isOnAdminSpace}
                showIdentityInfo={
                    isOnClientSpace ||
                    isOnAdminSpace ||
                    +stepId === stepIDs.CO_SUBSCRIBER_CONTACT_INFO
                }
            />
            <FullAddressForm
                address={forms.address}
                label={texts.ADRESSE_DU_DOMICILE}
                onAddressChange={callbacks.handleAddressChange}
                errors={errors}
                tunnelErrors={tunnelErrors}
                showInfoButton
            />
            <MemoizedFiscalAddressForm
                label={texts.ADRESSE_DU_DOMICILE_FISCAL}
                fiscalAddress={forms.fiscalAddress}
                hasDifferentFiscalAddress={forms.hasDifferentFiscalAddress}
                onChange={callbacks.handleFiscalAddressChange}
                onToggle={callbacks.toggleHasDifferentFiscalAddress}
                errors={errors}
                tunnelErrors={tunnelErrors}
            />
            <MemoizedSituationFormFPCIPP
                situation={forms.situation}
                onChange={callbacks.handleSituationChange}
                errors={errors}
                tunnelErrors={tunnelErrors}
            />
            <MemoizedFiscalSituationFormFCPRPP
                fiscalSituation={forms.fiscalSituation}
                fiscalObligations={forms.fiscalObligations}
                onChange={callbacks.handleFiscalSituationChange}
                onAddLine={callbacks.handleAddFiscalObligation}
                onRemoveLine={callbacks.handleRemoveFiscalObligation}
                onUpdateLine={callbacks.handleFiscalObligationChange}
                errors={errors}
                tunnelErrors={tunnelErrors}
            />
            <MemoizedPoliticallyExposedForm
                politicallyExposed={forms.politicallyExposed}
                onChange={callbacks.handleChangePoliticallyExposedPerson}
                onChangeReason={callbacks.handleChangePoliticallyExposedReason}
            />
            {showSuccessMessage && <SuccessMessage message={texts.YOUR_INFO_SAVED} />}
            <SubmitButton
                text={submitButtonText}
                onClick={
                    changesMade || ((isOnClientSpace || isOnAdminSpace) && changesMade)
                        ? handleSubmit
                        : handleNextStep
                }
                disabled={
                    (!allowSubmit && !tunnelSubscription.indirectUntransferred) || submitLoader
                }
                loading={submitLoader}
            />
        </TunnelBox>
    );
};

export default ContactInfoFormPP;
